import { type LiteralUnion } from 'type-fest'

import { type TranslationSchema } from '~/locales/resources'

export const indicatorGroups = ['fiveSegs', 'nineSegs', 'customized'] as const
export type IndicatorGroup = (typeof indicatorGroups)[number]

export const indicatorGroupLabelI18nKeyMap: Record<
  string,
  keyof TranslationSchema
> = {
  fiveSegs: '5segs_kpi',
  nineSegs: '9segs_kpi',
  customized: 'customize_kpi',
}

export const indicatorMap = {
  fiveSegs: [
    'rateRecognition',
    'currentUser',
    'loyal',
    'rateCv',
    'ratioLoyalToCurrentUser',
  ],
  nineSegs: ['npi', 'aNpi', 'nNpi', 'uNpi'],
  customized: [],
} as const satisfies Record<IndicatorGroup, readonly string[]>

export type Indicator = ValueOf<typeof indicatorMap>[number]

const percentageOnlyIndicators = [
  'rateRecognition',
  'rateCv',
  'ratioLoyalToCurrentUser',
  'npi',
  'aNpi',
  'nNpi',
  'uNpi',
] as const satisfies readonly Indicator[]
export type PercentageOnlyIndicator = Extract<
  Exclude<Indicator, 'distribution'>,
  (typeof percentageOnlyIndicators)[number]
>
export const isPercentageOnlyIndicator = (
  indicator: string,
): indicator is PercentageOnlyIndicator =>
  percentageOnlyIndicators.includes(indicator as PercentageOnlyIndicator)

type IndicatorWithDistribution = Indicator | 'distribution'
export type IndicatorWithCustomized = LiteralUnion<
  IndicatorWithDistribution,
  string
>

export const isIndicatorWithDistribution = (
  indicator: string,
): indicator is IndicatorWithDistribution =>
  indicator in indicatorLabelI18nKeyMap

export const indicatorLabelI18nKeyMap: Record<
  IndicatorWithDistribution,
  keyof TranslationSchema
> = {
  distribution: '9segs_customer_distribution',
  rateRecognition: 'aware',
  currentUser: 'existing_customer',
  loyal: 'loyal_customer',
  rateCv: 'user_conversion_rateamong_aware',
  ratioLoyalToCurrentUser: 'loyal_customersty_rateamong_existing_customers',
  npi: 'npi',
  aNpi: 'aNpi',
  nNpi: 'nNpi',
  uNpi: 'uNpi',
}

export const eventNameSuffixMap = {
  distribution: '_9segs distribution',
  rateRecognition: '_awareness',
  currentUser: '_current customer',
  loyal: '_loyal customer',
  rateCv: '_awareness to user conversion',
  ratioLoyalToCurrentUser: '_loyal customer ratio among current customer',
  npi: '_NPI',
  aNpi: '_a-NPI',
  nNpi: '_n-NPI',
  uNpi: '_u-NPI',
} as const satisfies Record<IndicatorWithDistribution, string>
